<template>
   <LoadingPage v-if="isLoading"></LoadingPage>
   <div class="proposals-page p-3">
      <!-- ── Page Header ── -->
      <div class="ms-page-header">
         <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
            <div class="d-flex align-items-center gap-3">
               <div class="ms-page-header__icon">
                  <i class="fa-solid fa-file-contract"></i>
               </div>
               <div>
                  <p class="ms-page-header__breadcrumb">Sales Pipeline <span class="ms-breadcrumb-sep">/</span> Proposals</p>
                  <div class="d-flex align-items-center flex-wrap gap-3 mt-1">
                     <h1 class="ms-page-header__title">Proposals</h1>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <div class="card">
         <div class="card-body m-2">
         <div class="row p-2 align-items-center">
            <div class="col-12 col-lg-3 mb-2 mb-lg-0">
               <input v-model.trim="search" type="text" class="form-control form-control-sm"
                  placeholder="🔍 Search organisation..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif; font-weight: 400;"
                  @keyup="onSearch()" />
            </div>

            <div class="col-12 col-lg-auto mb-2 mb-lg-0 px-lg-1">
               <button type="button" class="btn-primary-gradient"
                  style="font-size: 14px; height: 40px; padding: 0 1.5rem; display: inline-flex; align-items: center; justify-content: center; gap: 6px;"
                  title="Create a new proposal" @click="openCreateProposalModal()" :disabled="isLoading">
                  <i class="fa-solid fa-plus me-1"></i> Create Proposal
               </button>
            </div>

            <div class="col-12 col-lg-auto mb-2 mb-lg-0 px-lg-1" v-if="!$store.state.auth.isConsultant">
               <button type="button" class="btn-ghost-secondary"
                  style="font-size: 14px; height: 40px; padding: 0 1.5rem; display: inline-flex; align-items: center; justify-content: center; gap: 6px;"
                  title="Refresh item prices and swaps internal products for Auth-based ones for draft proposals" @click="refreshDraftPrices()" :disabled="isLoading">
                  <i class="fa-solid fa-arrows-rotate me-1"></i> Refresh Prices
               </button>
            </div>

            <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0">
            </div>

            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">
               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="sortBy in sortByList" :value="sortBy.value" :key="sortBy.value">{{ sortBy.text }}
                     </option>
                  </select>
               </div>
               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                     </option>
                  </select>
               </div>
               <div class="pagination">
                  <button @click="previousPage()" :disabled="pagination.page === 1" class="pagination-arrow">
                     <i class="fas fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page === pagination.totalPages || pagination.totalItems === 0"
                     class="pagination-arrow">
                     <i class="fas fa-chevron-right"></i>
                  </button>
               </div>
            </div>
         </div>

         <!-- Filters Section -->
         <div class="row p-2 pt-0 align-items-center">
            <div class="col-12 d-flex flex-wrap align-items-center gap-2">
               <!-- Status Filter Dropdown -->
               <div class="dropdown">
                  <button type="button" class="btn custom-filter-dropdown-btn btn-sm fw-bold"
                     style="height: 35px; border-radius: 8px !important; display: inline-flex; align-items: center;"
                     data-bs-toggle="dropdown" aria-expanded="false">
                     Status
                     <i class="fa-solid fa-filter ms-2" style="font-size: 12px; color: #00A0C1;"></i>
                     <span v-if="statusFilter.length > 0" class="badge ms-2 rounded-pill text-white" style="font-size: 10px; background-color: #00A0C1 !important;">
                        {{ statusFilter.length }}
                     </span>
                  </button>
                  <div class="dropdown-menu p-2" style="min-width: 220px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);" @click.stop>
                     <div class="d-flex flex-column gap-2 w-100" style="max-height: 250px; overflow-y: auto;">
                        <div v-for="opt in statusOptions" :key="opt.value" class="d-flex align-items-center mb-1">
                           <label class="d-flex align-items-center cursor-pointer mb-0 w-100">
                              <input class="custom-checkbox-input" type="checkbox" :id="'proposal-status-' + opt.value" :value="opt.value" v-model="statusFilter" style="pointer-events: auto;">
                              <span class="custom-checkbox"></span>
                              <span class="ms-2 select-none" style="font-size: 14px; font-weight: 500; color: #495057;">{{ opt.text }}</span>
                           </label>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- Lead Filter Dropdown -->
               <div class="dropdown">
                  <button type="button" class="btn custom-filter-dropdown-btn btn-sm fw-bold"
                     style="height: 35px; border-radius: 8px !important; display: inline-flex; align-items: center;"
                     data-bs-toggle="dropdown" aria-expanded="false">
                     Organisation
                     <i class="fa-solid fa-filter ms-2" style="font-size: 12px; color: #00A0C1;"></i>
                     <span v-if="leadFilter.length > 0" class="badge ms-2 rounded-pill text-white" style="font-size: 10px; background-color: #00A0C1 !important;">
                        {{ leadFilter.length }}
                     </span>
                  </button>
                  <div class="dropdown-menu p-2" style="min-width: 250px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);" @click.stop>
                     <div class="d-flex flex-column gap-2 w-100" style="max-height: 250px; overflow-y: auto;">
                        <div v-for="(l, idx) in leads" :key="l.id || idx" class="d-flex align-items-center mb-1">
                           <label class="d-flex align-items-center cursor-pointer mb-0 w-100">
                              <input class="custom-checkbox-input" type="checkbox" :id="'proposal-lead-' + (l.id || idx)" :value="l.id" v-model="leadFilter" style="pointer-events: auto;">
                              <span class="custom-checkbox"></span>
                              <span class="ms-2 select-none" style="font-size: 14px; font-weight: 500; color: #495057;">
                                 {{ l.organisation_name || `${l.first_name} ${l.last_name}` }}
                              </span>
                           </label>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- Active Filters Chips -->
               <div v-if="statusFilter.length > 0 || leadFilter.length > 0" class="d-flex flex-wrap gap-2 align-items-center ms-3">
                  <span class="small text-muted fw-bold">Active Filters:</span>
                  <!-- Status Chips -->
                  <span v-for="val in statusFilter" :key="'status-' + val" class="badge bg-info text-white p-2 d-inline-flex align-items-center gap-1" style="border-radius: 20px; font-size: 12px; font-weight: 500; background-color: #00A0C1 !important;">
                     {{ getStatusText(parseInt(val, 10), null) }}
                     <i class="fa-solid fa-xmark cursor-pointer ms-1" style="font-size: 10px;" @click="removeStatusFilter(val)"></i>
                  </span>
                  <!-- Lead Chips -->
                  <span v-for="val in leadFilter" :key="'lead-' + val" class="badge bg-info text-white p-2 d-inline-flex align-items-center gap-1" style="border-radius: 20px; font-size: 12px; font-weight: 500; background-color: #00A0C1 !important;">
                     {{ getLeadDisplayName(val) }}
                     <i class="fa-solid fa-xmark cursor-pointer ms-1" style="font-size: 10px;" @click="removeLeadFilter(val)"></i>
                  </span>
                  <button type="button" class="btn btn-link btn-sm text-decoration-none p-0 ms-2" style="font-size: 12px; color: #00A0C1;" @click="clearFilter()">Clear All</button>
               </div>
            </div>
         </div>
         <div class="row p-2">
            <table class="table align-middle mainTable">

               <thead class="align-middle">
                  <tr>
                     <th scope="col" v-for="header in headers" :key="header.value">{{ header.text }}</th>
                  </tr>
               </thead>

               <TableEmptyState
                  v-if="this.pagination.totalItems <= 0"
                  :colspan="headers.length"
                  icon="fa-solid fa-file-contract"
                  title="No Proposals Found"
                  description="There are currently no proposals created. Create a new proposal to start presenting quotes and agreements to your clients."
                  action-text="Create Proposal"
                  action-icon="fa-solid fa-plus"
                  :is-filtered="isFiltered"
                  filtered-title="No Matching Proposals Found"
                  filtered-description="No proposals match your current search query or active filter criteria. Try adjusting or clearing your filters."
                  clear-filters-text="Reset Filters & Search"
                  @action="openCreateProposalModal"
                  @clear-filters="clearAllFiltersAndSearch"
               />

                <tbody v-else>
                   <tr v-for="(proposal) in proposals" :key="proposal.id" class="clickable-row" title="Click to open in new tab" @click="openProposalInNewTab(proposal.id)">
                      <td :style="proposal.shared_users && proposal.shared_users.length > 0 ? 'border-left: 3px solid #0dcaf0; padding-left: 12px;' : ''"
                           :title="proposal.shared_users && proposal.shared_users.length > 0 ? getSharedEmailsTooltip(proposal.shared_users) : null">
                         <span class="spark-pill-badge"
                            :class="getVersionBadgeClass(proposal.proposal_version)"
                            :title="getProposalTooltip(proposal)">

                            <i v-if="proposal.renewed_from_proposal_uuid" class="fa-solid fa-rotate-left"></i>
                            <i v-else-if="proposal.is_extended" class="fa-solid fa-calendar-plus"></i>
                            <i v-else class="fa-solid fa-code-branch"></i>

                            {{ proposal.proposal_version }}
                         </span>
                      </td>
                      <td><span class="user-initials-icon"
                            :style="{ backgroundColor: getColorFromName(getVertexAuthUser(proposal.created_by)) }"
                            :title="this.getVertexAuthUser(proposal.created_by)">
                            {{ this.retrieveUserIcon(this.getVertexAuthUser(proposal.created_by)) }}
                         </span>
                      </td>
                      <td>
                         <span>
                            {{ 
                                (proposal.auth_organisation && proposal.auth_organisation.name)
                                ? proposal.auth_organisation.name
                                : ((proposal.contract_acceptances && proposal.contract_acceptances.length > 0 && proposal.contract_acceptances[0].organisation_name != "") 
                                   ? proposal.contract_acceptances[0].organisation_name 
                                   : ((proposal.Lead != undefined && proposal.Lead.organisation_name != undefined && proposal.Lead.organisation_name != "") ? proposal.Lead.organisation_name : "No Org Name Set"))
                             }}
                          </span>
                          <span v-if="proposal.auth_organisation && proposal.auth_organisation.name && !proposal.is_auth_sync" class="spark-pill-badge spark-pill-badge--linked ms-1" :title="'Original Lead Name: ' + (proposal.Lead?.original_organisation_name || proposal.Lead?.organisation_name || 'N/A')">
                             <i class="fa-solid fa-link"></i> Linked
                          </span>
                          <div v-if="proposal.Lead && proposal.Lead.original_organisation_name && proposal.Lead.original_organisation_name !== '' && proposal.Lead.original_organisation_name !== ((proposal.contract_acceptances && proposal.contract_acceptances.length > 0 && proposal.contract_acceptances[0].organisation_name) ? proposal.contract_acceptances[0].organisation_name : proposal.Lead.organisation_name)" class="text-muted fst-italic mt-1" style="font-size: 11px;">
                             <i class="fa-solid fa-history me-1"></i>Formerly: <strong>{{ proposal.Lead.original_organisation_name }}</strong>
                          </div>
                       </td>

                      <td><b>{{ formatCurrency(proposal.proposal_total_amount) }}</b></td>
                      <td><b>{{ formatCurrency(proposal.proposal_accepted_amount) }} </b></td>
                      <td>
                         <span class="spark-pill-badge"
                            :class="getProposalStatusBadgeClass(proposal.proposal_status)">
                             <i :class="getProposalStatusIcon(proposal.proposal_status)"></i>
                             {{ getStatusText(proposal.proposal_status) }}
                         </span>
                      </td>
                      <td>{{ formatDate(proposal.created_at) }}</td>
                     <td :class="getStatusClass(formatDate(proposal.sent_at), 1)">{{
                        formatDate(proposal.sent_at) || "Not Sent" }}</td>
                     <td :class="getStatusClass(formatDate(proposal.expiry_date), 4)">{{
                        formatDate(proposal.expiry_date) || "Not Sent" }}</td>
                     <td :class="getStatusClass(formatDate(proposal.accepted_at), 4)">{{
                        formatDate(proposal.accepted_at) || "TBD" }}</td>
                     <td @click.stop>
                        <div class="action-button-container">
                           <button type="button" class="btn-svg-icon" title="Click to preview and edit proposal"
                              @click="onButtonClick('view-edit', proposal.id)">
                              <img src="@/assets/img/icons/universal_edit.svg" alt="Edit" class="icon-svg" />
                           </button>

                            <button :disabled="proposal.proposal_status == 3 || proposal.proposal_status == 4 || proposal.proposal_status == 6" v-if="!this.$store.state.auth.isGeneralUser && this.$store.getters['auth/canSendProposal']" type="button" class="btn-svg-icon"
                              :title="(proposal.proposal_status == 3) ? 'Proposal already accepted' : (proposal.proposal_status == 4 ? 'Proposal declined' : (proposal.proposal_status == 6 ? 'Proposal completed' : 'Click to send'))"
                              @click="onButtonClick('send', proposal); openSendModal();">
                              <img src="@/assets/img/icons/send.svg" alt="Send" class="icon-svg" />
                           </button>

                           <button type="button" class="btn-svg-icon" title="View Client Proposal Page"
                              @click="onButtonClick('view-client', proposal)">
                              <img src="@/assets/img/icons/Landingpage.svg" alt="View Client Proposal"
                                 class="icon-svg" />
                           </button>

                           <button type="button" class="btn-svg-icon btn-contract-icon"
                              :disabled="getContractCount(proposal.id) === 0"
                              :title="getContractButtonTitle(proposal)"
                              @click="onButtonClick('view-contracts', proposal)">
                              <i class="fa-solid fa-file-contract icon-svg-fa"></i>
                              <span v-if="getContractCount(proposal.id) > 1" class="contract-count-badge">{{ getContractCount(proposal.id) }}</span>
                           </button>

                           <button type="button" class="btn-svg-icon" title="View Activity Log"
                              @click="onButtonClick('activity-log', proposal.id)">
                              <img src="@/assets/img/icons/actlog.svg" alt="Activity Log" class="icon-svg" />
                           </button>

                           <button type="button" class="btn-svg-icon btn-svg-icon--delete"
                              :title="(proposal.proposal_status != 0) ? 'Cannot delete a sent proposal' : 'Click to delete'"
                              @click="onButtonClick('delete', proposal.id)" :disabled="proposal.proposal_status != 0">
                              <img src="@/assets/img/icons/universal_delete.svg" alt="Delete" class="icon-svg" />
                           </button>
                        </div>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>


   <div class="modal fade" id="sendForm" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static" ref="sendForm"
      style="top: 2%;">
      <div ref="modalSendFormDialogRef" class="modal-dialog modal-dialog-scrollable">
         <div class="modal-content">
            <div class="modal-header justify-content-end">
               <div>
                  <button ref="closeSendFormModal" type="button" class="btn-close" data-bs-dismiss="modal"
                     aria-label="Close"></button>
               </div>
            </div>

            <div class="modal-body">
               <SendForm ref="sendProposalForm" :initialSendTo="proposal_client_email" :proposalID="proposal_id"
                  :hasEditor="false" :initialProposalTitle="proposal_title"
                  @proposalSentSuccess="this.init(); this.$refs.closeSendFormModal.click(); this.getProposalsList()" />
            </div>
         </div>
      </div>
   </div>

   <!-- ========== CONTRACT SELECTION MODAL ========== -->
   <div v-if="isContractModalOpen" class="contract-overlay" @click.self="isContractModalOpen = false">
      <div class="contract-select-modal">
         <div class="contract-select-header">
            <h5 class="contract-select-title">Select Contract</h5>
            <button @click="isContractModalOpen = false" class="contract-select-close">&times;</button>
         </div>
         <div class="contract-select-body">
            <div v-for="contract in selectedProposalContracts" :key="contract.id" class="contract-card"
               @click="openContractView(contract)">
               <div class="contract-card-info">
                  <p class="contract-card-name">{{ contract.contract_title || 'Untitled Contract' }}</p>
                  <p class="contract-card-detail"><strong>Status:</strong> <span :style="{ color: getContractStatusColor(contract.contract_status) }">{{ getContractStatusText(contract.contract_status) }}</span></p>
                  <p class="contract-card-detail" v-if="contract.contract_version"><strong>Version:</strong> {{ contract.contract_version }}</p>
                  <p class="contract-card-detail"><strong>Created:</strong> {{ formatDate(contract.created_at) }}</p>
               </div>
            </div>
            <div v-if="selectedProposalContracts.length === 0" class="contract-no-results">
               <p>No contracts found.</p>
            </div>
         </div>
      </div>
   </div>

   <div class="modal fade" id="activityLogModal" ref="activityLogModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-scrollable">
         <div class="modal-content">
            <div class="modal-header activity-log-header">
               <h5 class="modal-title">Activity Log</h5>
               <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <hr class="modal-divider">

            <div class="modal-body">
               <ProposalActivity ref="proposalActivityRef" />
            </div>
         </div>
      </div>
   </div>


    <div class="modal fade modal-blur" id="createProposalModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static"
      ref="createProposalModalRef" style="top: 2%">
       <div class="blur-backdrop"></div>
       <div ref="createProposalModalDialogRef" class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            :class="[isLeadSelected ? 'modal-custom-width' : 'modal-md-width']">
          <div class="modal-content" style="border-radius: 16px; border: none; box-shadow: 0 10px 30px rgba(0,0,0,0.25); background-color: #fff; min-height: 600px; max-height: 92vh;">
             <!-- Always-static Close Button -->
             <button type="button" class="close-button-custom" @click="closeCreateProposalModal" aria-label="Close">&times;</button>

             <!-- Static Modal Header -->
             <div class="px-4 pt-4 pb-0 text-start">
                <h5 class="modal-title-custom" style="color: #364A63; font-weight: bold; font-size: 1.25rem;">Create Proposal</h5>
                <hr style="margin-top: 1rem; margin-bottom: 0.5rem; border-color: #dee2e6;">
             </div>

             <div class="modal-body px-4 pb-4 pt-2 animate-pop-in" style="background-color: transparent !important; max-height: calc(90vh - 110px); overflow-y: auto !important;">
                <VertexProposal :lead_id="selectedLeadID" ref="createProposalRef" @lead-changed="handleLeadChanged" />
             </div>
          </div>
       </div>
    </div>

   <ConfirmModal
      v-model="showConfirmModal"
      :title="confirmModalTitle"
      :message="confirmModalMessage"
      @confirm="onConfirmAction"
   />
   </div>
</template>

<script>
import moment from "moment";
import { debounce, truncateLongText, log } from '@/plugins/helper.js'
import LoadingPage from '@/components/Utilities/vertex-loading.vue'
//import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";
import SendForm from '@/components/Proposals/SendForm.vue';
import { Modal } from 'bootstrap'; // Import Bootstrap's Modal class

// 1. Import the ProposalActivity component
import ProposalActivity from '@/components/Proposals/ProposalActivity.vue';
import VertexProposal from '@/components/Proposals/VertexProposal.vue';
import TableEmptyState from '@/components/Utilities/TableEmptyState.vue';


export default {

   components: {
      LoadingPage,
      SendForm,
      ProposalActivity, // 2. Register the component
      VertexProposal,
      TableEmptyState
   },

   data() {
      return {
         headers: [
            { text: "Version", value: "version" },
            { text: "Owner", value: "owner" },
            { text: "Organisation", value: "organisation_name" },
            { text: "Total Amount", value: "total_amount" },
            { text: "Accepted Amount", value: "accepted_amount" },
            { text: "Status", value: "status" },
            { text: "Date Created", value: "date_created" },
            { text: "Date Sent", value: "date_sent" },
            { text: "Expiry", value: "expiry_date" },
            { text: "Date Accepted", value: "date_accepted" },
            { text: "Action", value: "action" },
         ],
         search: "",
         leads: [],
         products: [],
         proposals: [],
         pagination: {
            pageSize: 10,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "5", value: 5 },
            { text: "10", value: 10 },
            { text: "20", value: 20 },
         ],
         sortByList: [
            { text: "Newest", value: "id_desc" },
            { text: "Oldest", value: "id_asc" },
         ],
         filterByList: [
            { text: "None", value: "" },
            { text: "Status", value: "status" },
         ],
         sortBy: "id_desc", //default option
         statusFilter: ["0", "1", "2", "3", "5"],
         leadFilter: [],
         statusOptions: [
            { text: "Draft", value: "0" },
            { text: "Sent", value: "1" },
            { text: "Resent", value: "2" },
            { text: "Accepted", value: "3" },
            { text: "Declined", value: "4" },
            { text: "Expired", value: "5" },
            { text: "Completed", value: "6" },
         ],
         filterByPriority: "none",
         filterByStatus: "none",
         sorting: {
            field: "id",
            order: "desc",
            filter: "none"
         },
         isEditing: false,
         debounceFn: null,
         leadStatusTypes: [],
         leadPriorityTypes: [],
         isLeadStatusProposalSent: false,
         totalProposals: 0,
         markedForAuthCreationOption: {
            id: 'forCreationInAuth',
            name: 'Marked for creation in Auth'
         },
         authOrganisationsList: [],
         isViewSpamOnly: false,
         filtering: {
            field: "",
            value: "none"
         },
         filterBy: "status",
         isFilterByStatus: false,
         isLoading: false,
         colorPalette: [
            '#FF5733', '#33FF57', '#3357FF', '#FF33A1', '#A133FF', '#33FFF2', // Vibrant colors
            '#FFC300', '#DAF7A6', '#C70039', '#900C3F', '#581845', '#FF8C00', // More diverse
            '#4CAF50', '#2196F3', '#9C27B0', '#FFEB3B', '#FF9800', '#795548'  // Material-like
         ],
         colorForVersionIcon: {
            minor: "#FFA500",
            major: "#28a745",
            default: "#6c757d"
         },
         proposal_client_email: "",
         proposal_id: "",
         proposal_title: "",
         sendProposalForm: null,
         proposalContractsMap: {},
         selectedProposalContracts: [],
         isContractModalOpen: false,
         colorForStatus: {
            pending: '#F3994D', // Orange
            sent: '#00a0c1',    // Blue
            resent: '#00a0c1',   // Blue
            accepted: '#00A54F',// Green
            rejected: '#EB1C24', // Red
            completed: '#6C757D', // Grey
         },
         vtxUsers: [],
         ws: null,
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingAction: null,
         pendingActionData: null,
         createProposalModalInstance: null,
         selectedLeadID: "",
         isLeadSelected: false,
      }

   },

   created() {
      this.init()

      // for setting the delay of calling the function when searching
      this.debounceFn = debounce(() => this.getProposalsList(), 500)
   },

   mounted() {
      this.sendProposalForm = new Modal(this.$refs.sendForm);
      this.activityLogModalInstance = new Modal(this.$refs.activityLogModal); // 3. Initialize the activity log modal
      this.createProposalModalInstance = new Modal(this.$refs.createProposalModalRef);

      if (this.$refs.createProposalModalRef) {
         this.$refs.createProposalModalRef.addEventListener('hidden.bs.modal', () => {
            if (this.$route.query && (this.$route.query.create || this.$route.query.org_id || this.$route.query.lead_id || this.$route.query.contact_id || this.$route.query.contact_uuid || this.$route.query.contact_email)) {
               this.$router.replace({ path: '/proposals' });
            }
         });
      }

      this.checkRouteForCreateProposal();

      // Use wss:// for https, ws:// for local http, dynamically based on window location
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/ws`;

      this.ws = new WebSocket(wsUrl);

      this.ws.onmessage = (event) => {
         const data = JSON.parse(event.data);

         if (data.event === "price_refresh_completed") {
            this.isLoading = false;
            this.$toast.success("Draft prices and Auth relationships have been fully synced!");
            this.getProposalsList(); // Refresh the table automatically
         } else if (data.event === "price_refresh_failed") {
            this.isLoading = false;
            this.$toast.error("Background price sync failed.");
         }
      };

   },

   beforeUnmount() {
      if (this.ws) {
         this.ws.close();
      }
   },

   computed: {
      isFiltered() {
         return (this.search && this.search.trim().length > 0) ||
                this.statusFilter.length > 0 ||
                this.leadFilter.length > 0;
      },

      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)

         return start + " - " + end
      },

      currentPage() {
         return this.pagination.page
      },

      rowsPerPage() {
         return this.pagination.pageSize
      },

      sortItems() {
         return this.sortBy
      },

      filterByField() {
         return this.filterBy
      },

      filterItems() {
         if (this.filterBy == "priority") {
            return this.filterByPriority
         } else if (this.filterBy == "status") {
            return this.filterByStatus
         }

         return "none"
      },
   },

   watch: {
      'pagination.pageSize'() {
         this.pagination.page = 1
         this.getProposalsList()
      },
      'pagination.page'() {
         this.getProposalsList()
      },
      statusFilter: {
         handler() {
            this.pagination.page = 1
            this.getProposalsList()
         },
         deep: true
      },
      leadFilter: {
         handler() {
            this.pagination.page = 1
            this.getProposalsList()
         },
         deep: true
      },
      filterBy() {
         if (this.filterBy === "") {
            this.statusFilter = ["0", "1", "2", "3", "5"]
         }
         this.pagination.page = 1
         this.getProposalsList()
      },
      '$route.query': {
         handler() {
            this.checkRouteForCreateProposal();
         },
         deep: true
      },
   },

   methods: {

      getProposalTooltip(proposal) {
         let titles = [];
         if (proposal.renewed_from_proposal_uuid) titles.push('Re-issued proposal');
         if (proposal.is_extended) titles.push('Expiry date has been extended');
         return titles.join(' | ');
      },

      openSendModal() {
         this.sendProposalForm.show();
      },

      getVersionIconColor(versionNumber) {
         if (typeof versionNumber !== 'string' || !versionNumber.startsWith('v')) {
            return this.colorForVersionIcon.default;
         }

         const parts = versionNumber.slice(1).split('.');
         if (parts.length !== 2) {
            return this.colorForVersionIcon.default;
         }
         const minorStr = parts[1];
         const minor = parseInt(minorStr, 10);
         if (isNaN(minor) || minor.toString() !== minorStr) {
            return this.colorForVersionIcon.default;
         }

         if (minor > 0) {
            return this.colorForVersionIcon.minor;
         } else {
            return this.colorForVersionIcon.major;
         }
      },

      getColorFromName(name) {
         if (!name) {
            return '#6c757d';
         }

         let hash = 0;
         for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
         }

         const colorIndex = Math.abs(hash) % this.colorPalette.length;
         return this.colorPalette[colorIndex];
      },

      retrieveUserIcon(fullName) {
         let icon = "";

         if (fullName) {
            const nameParts = fullName.split(' ');

            if (nameParts.length > 0 && nameParts[0] !== '') {
               icon += nameParts[0].charAt(0);
            }

            if (nameParts.length > 1 && nameParts[1] !== '') {
               icon += nameParts[1].charAt(0);
            }
         }

         return icon.toUpperCase();
      },

      isProposalSent(versionNumber) {
         if (typeof versionNumber !== 'string' || !versionNumber.startsWith('v')) {
            return false;
         }

         const parts = versionNumber.slice(1).split('.');
         if (parts.length !== 2) {
            return false;
         }

         const minorStr = parts[1];
         const minor = parseInt(minorStr, 10);

         if (isNaN(minor) || minor.toString() !== minorStr) {
            return false;
         }

         return minor <= 0;
      },

      getAcceptType(accept_code) {
         switch (accept_code) {
            case 1: {
               return "Yes"
            }

            default: return "TBD"
         }
      },

      formatCurrency(value) {
         const numericValue = parseFloat(value);
         if (isNaN(numericValue)) {
            return '$0.00';
         }
         return `$${numericValue.toFixed(2)}`;
      },

      truncateLongText(text, limit) {
         return truncateLongText(text, limit)
      },

      init() {
         this.getProposalsList()
         this.getVertexUsers()
         this.getLeadsList()
      },

      async sendProposal(id) {
         log("proposal_id", id)

         await this.$axios.put("/proposals/" + id + "/send")
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message)
                  this.getProposalsList()
               } else {
                  this.$toast.error(response.data.message)
               }
            })
            .catch((error) => {
               log(error)
               this.$toast.error(error.response.data.message)
            })
      },

      getSharedEmailsTooltip(sharedUsers) {
         if (!sharedUsers || sharedUsers.length === 0) return "";
         return "Shared with:\n" + sharedUsers.map(u => u.email).join("\n");
      },

      onButtonClick(action, data) {
         if (action == "send") {
            // data is the full proposal object
            this.proposal_client_email = ""
            this.proposal_client_email = data.Lead.email
            this.proposal_id = data.id
            this.proposal_title = data.proposal_title
            // this.sendProposal(data.id) is handled by the modal now
         } else if (action == "view-edit") {
            // data is the proposal id
            this.$router.push({ name: 'proposal-view', params: { id: data } })
         } else if (action == "delete") {
            // data is the proposal id
            this.deleteProposal(data)
         } else if (action == "view-client") {
            // data is the full proposal object
            this.viewClientProposal(data);
         } else if (action == "view-contracts") {
            // data is the full proposal object
            this.handleViewContracts(data)
         } else if (action == "activity-log") {
            // 4. Handle the 'activity-log' click
            // The 'data' variable here is the proposal.id
            this.$refs.proposalActivityRef.init(data); // Initialize the child component with the ID
            this.activityLogModalInstance.show(); // Show the modal
         }
      },

      checkRouteForCreateProposal() {
         const q = this.$route.query;
         if (q && (q.create === 'true' || q.create === true || q.org_id || q.lead_id || q.contact_id || q.contact_uuid || q.contact_email)) {
            this.$nextTick(() => {
               if (this.createProposalModalInstance && this.$refs.createProposalRef) {
                  this.openCreateProposalModal({
                     orgId: q.org_id || '',
                     leadId: q.lead_id || '',
                     contactId: q.contact_id || '',
                     contactUuid: q.contact_uuid || '',
                     contactEmail: q.contact_email || ''
                  });
               }
            });
         }
      },

      openCreateProposalModal(options = null) {
         this.selectedLeadID = "";
         this.isLeadSelected = false;
         if (this.$refs.createProposalRef) {
            this.$refs.createProposalRef.init("", options);
         }
         if (this.createProposalModalInstance) {
            this.createProposalModalInstance.show();
         }
      },

      closeCreateProposalModal() {
         if (this.createProposalModalInstance) {
            this.createProposalModalInstance.hide();
         }
         if (this.$route.query && (this.$route.query.create || this.$route.query.org_id || this.$route.query.lead_id || this.$route.query.contact_id || this.$route.query.contact_uuid || this.$route.query.contact_email)) {
            this.$router.replace({ path: '/proposals' });
         }
         this.getProposalsList();
      },

      handleLeadChanged(lead) {
         this.isLeadSelected = !!lead;
      },

      openProposalInNewTab(id) {
         const routeData = this.$router.resolve({ name: 'proposal-view', params: { id: id } });
         window.open(routeData.href, '_blank');
      },

      viewClientProposal(proposal) {
         // This function opens the client-facing proposal link in a new tab.
         // NOTE: This requires 'unique_key' and 'internal_access_key' to be present in the proposal object.
         if (proposal && proposal.unique_key && proposal.internal_key) {
            const routeData = this.$router.resolve({ name: 'clientProposal', params: { unique_key: proposal.unique_key, access_key: proposal.internal_key } });
            window.open(routeData.href, '_blank');
         } else {
            this.$toast.error("Could not generate client link. Proposal data is missing required keys.")
            console.error("Proposal object is missing unique_key or internal_access_key", proposal);
         }
      },

      async deleteProposal(id) {
         this.pendingAction = 'deleteProposal';
         this.pendingActionData = id;
         this.confirmModalTitle = 'Delete Proposal';
         this.confirmModalMessage = 'Do you want to delete this proposal?';
         this.showConfirmModal = true;
      },

      onToggleSidebar() {
         this.$refs.leadSidebarRef.addEventListener('hidden.bs.offcanvas', () => {
            this.$router.push({ name: 'leads' })
         });
      },

      onSearch() {
         this.pagination.page = 1
         this.debounceFn()
      },

      toggleProposalUpload({ id }) {
         this.isLeadStatusProposalSent = true
         this.$refs.leadProposalsRef.showModal(this.$refs.leadProposals)

         this.$refs.leadProposals.addEventListener('hidden.bs.modal', () => {
            this.isLeadStatusProposalSent = false
         });

         this.$refs.leadProposalsRef.init(id)
      },

      async getProposalsList() {
         this.isLoading = true
         let apiPath = "/proposals?pageSize=" + this.pagination.pageSize + "&page=" + this.pagination.page

         if (this.sortBy != "dateCreated_desc") {
            apiPath += "&sort=" + this.sorting.field

            if (this.sorting.order != "") {
               console.log(this.sorting.order)
               apiPath += "&order=" + this.sorting.order
            }
         }

         if (this.search != "") {
            apiPath += "&search=" + this.search
         }

         // Apply selected status filter
         if (this.filterBy === "status") {
            if (this.statusFilter && this.statusFilter.length > 0) {
               apiPath += "&status=" + this.statusFilter.join(",")
            }
         } else {
            apiPath += "&excludeStatus=4,6"
         }

         if (this.leadFilter && this.leadFilter.length > 0) {
            apiPath += "&lead=" + encodeURIComponent(this.leadFilter.join(","))
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               this.proposals = response.data.vertex_proposals
               log(this.proposals)
               this.pagination.totalItems = response.data.total_items
               this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.pageSize) || 1;
               this.isLoading = false
               this.fetchAllProposalContracts()
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
               this.isLoading = false
            })

      },

      formatDate(date) {

         if (!date || !moment(date).isValid() || moment(date).year() < 1980) {
            return null;
         }

         return moment(date).format("DD-MMM-YYYY")
      },

      formatTimestamp(date) {
         if (!date || !moment(date).isValid() || moment(date).year() < 1980) {
            return null;
         }
         return moment(date).format("DD-MMM-YYYY @ HH:mm:ss");
      },

      previousPage() {
         if (this.pagination.page > 1) { this.pagination.page-- }
      },

      nextPage() {
         if (this.pagination.page < this.pagination.totalPages) { this.pagination.page++ }
      },

      priorityClass(value) {
         let priority = this.leadPriorityTypes.filter((p) => { return p.id == value }).map((e) => e.name)

         return priority.toString().toLowerCase()
      },

      clearFilter() {
         this.statusFilter = []
         this.leadFilter = []
      },

      clearAllFiltersAndSearch() {
         this.search = '';
         this.statusFilter = [];
         this.leadFilter = [];
         this.pagination.page = 1;
         this.getProposalsList();
      },

      removeStatusFilter(val) {
         this.statusFilter = this.statusFilter.filter(v => v !== val)
      },

      removeLeadFilter(val) {
         this.leadFilter = this.leadFilter.filter(v => v !== val)
      },

      async getLeadsList() {
         await this.$axios.get("/leads?pageSize=200")
            .then((response) => {
               this.leads = response.data.leads || []
            })
            .catch((error) => {
               console.error("Failed to load leads", error)
            })
      },

      getLeadDisplayName(uuid) {
         if (!this.leads) return uuid
         const lead = this.leads.find(l => l.id === uuid || l.uuid === uuid)
         if (!lead) return uuid
         if (lead.organisation_name) return lead.organisation_name
         return `${lead.first_name} ${lead.last_name}`
      },

      setFilterOptions(filter_field) {
         switch (filter_field) {
            case "priority": {
               this.filterValueOptions = [{
                  id: "none",
                  name: "All"
               }]
               this.leadPriorityTypes.forEach((s) => {
                  this.filterValueOptions.push({
                     id: s.id,
                     name: s.display_name,
                  })
               })
               return;
            }
            case "status": {
               this.filterValueOptions = [{
                  id: "none",
                  name: "All"
               }]
               this.leadStatusTypes.forEach((s) => {
                  this.filterValueOptions.push({
                     id: s.id,
                     name: s.display_name,
                  })
               })
               return;
            }
         }
      },

      getVertexAuthUser(id) {
         // console.log(id)
         // return
         if (!this.vtxUsers || !Array.isArray(this.vtxUsers)) return "System Generated"
         let user = this.vtxUsers.find(user => user && user.id === id);
         if (user) {
            return user.first_name + " " + user.last_name
         } else if (user == null && id != "") {
            return "Deleted User"
         } else {
            return "System Generated"
         }
      },

      getVertexUsers() {
         this.vtxUsers = this.$store.state.auth.usersWithVertexRoles || []
      },

      getStatusColor(status_code) {
         switch (Number(status_code)) {
            case 0: // Pending
               return this.colorForStatus.pending;
            case 1: // Sent
               return this.colorForStatus.sent;
            case 2: // Resent
               return this.colorForStatus.resent;
            case 3: // Accepted
               return this.colorForStatus.accepted;
            case 4: // Declined
               return this.colorForStatus.rejected;
            case 5: // Expired
               return this.colorForStatus.rejected;
            case 6: // Completed
               return this.colorForStatus.completed;
            default:
               return this.colorForStatus.pending;
         }
      },

      getStatusText(status_code) {
         switch (Number(status_code)) {
            case 1:
               return "Sent";
            case 2:
               return "Resent";
            case 3:
               return "Accepted";
            case 4:
               return "Declined";
            case 5:
               return "Expired";
            case 6:
               return "Completed";
            default: // Handles 0 and any other case
               return "Draft";
         }
      },

      getVersionBadgeClass(versionNumber) {
         if (typeof versionNumber !== 'string' || !versionNumber.startsWith('v')) {
            return 'spark-pill-badge--version-default';
         }
         const parts = versionNumber.slice(1).split('.');
         if (parts.length !== 2) return 'spark-pill-badge--version-default';
         const minor = parseInt(parts[1], 10);
         if (isNaN(minor)) return 'spark-pill-badge--version-default';
         return minor > 0 ? 'spark-pill-badge--version-minor' : 'spark-pill-badge--version-major';
      },

      getProposalStatusBadgeClass(statusCode) {
         switch (Number(statusCode)) {
            case 1: return 'spark-pill-badge--sent';
            case 2: return 'spark-pill-badge--resent';
            case 3: return 'spark-pill-badge--accepted';
            case 4: return 'spark-pill-badge--declined';
            case 5: return 'spark-pill-badge--expired';
            case 6: return 'spark-pill-badge--completed';
            default: return 'spark-pill-badge--draft';
         }
      },

      getProposalStatusIcon(statusCode) {
         switch (Number(statusCode)) {
            case 1: return 'fa-solid fa-paper-plane';
            case 2: return 'fa-solid fa-rotate-right';
            case 3: return 'fa-solid fa-circle-check';
            case 4: return 'fa-solid fa-circle-xmark';
            case 5: return 'fa-solid fa-calendar-xmark';
            case 6: return 'fa-solid fa-check-double';
            default: return 'fa-solid fa-pen';
         }
      },

      getStatusClass(value, type) {
         let base = "status-text"
         switch (type) {
            case 1: { // Date Sent
               if (value === null)
                  return base + " text-red"
            } break;
            case 2: {
               console.log("")
            } break;
            case 3: { // Acceptance Type
               if (value === 0) {
                  return base + " text-grey"
               }
            } break;
            case 4: {
               if (value === null)
                  return base + " text-red"
            } break;
            case 5: {
               if (value === null)
                  return base + " text-red"
            }
         }

         return base + " text-green"
      },

      // ---- Contract helpers ----
      getContractCount(proposalId) {
         return (this.proposalContractsMap[proposalId] || []).length
      },

      getContractButtonTitle(proposal) {
         const count = this.getContractCount(proposal.id)
         if (count === 0) return 'No contracts linked'
         if (count === 1) return 'View Contract'
         return `View Contracts (${count})`
      },

      async handleViewContracts(proposal) {
         const cached = this.proposalContractsMap[proposal.id]
         if (cached && cached.length === 1) {
            this.openContractView(cached[0])
         } else if (cached && cached.length > 1) {
            this.selectedProposalContracts = cached
            this.isContractModalOpen = true
         } else {
            // Fetch on-demand if not cached
            try {
               const res = await this.$axios.get(`/proposals/${proposal.id}/contracts`)
               const contracts = res.data.vertex_contracts || []
               this.proposalContractsMap = { ...this.proposalContractsMap, [proposal.id]: contracts }
               if (contracts.length === 1) {
                  this.openContractView(contracts[0])
               } else if (contracts.length > 1) {
                  this.selectedProposalContracts = contracts
                  this.isContractModalOpen = true
               } else {
                  this.$toast.info('No contracts are linked to this proposal.')
               }
            } catch (e) {
               this.$toast.error('Failed to load contracts.')
            }
         }
      },

      openContractView(contract) {
         const routeData = this.$router.resolve({ name: 'contract-view', params: { id: contract.id } })
         window.open(routeData.href, '_blank')
      },

      getContractStatusColor(status) {
         switch (status) {
            case 0: return '#6C757D'
            case 1: return '#00A0C1'
            case 2: return '#17A2B8'
            case 3: return '#FFC107'
            case 4: return '#28A745'
            case 5: return '#DC3545'
            case 6: return '#FD7E14'
            case 7: return '#20C997'
            case 8: return '#DC3545'
            default: return '#6C757D'
         }
      },

      getContractStatusText(status) {
         switch (status) {
            case 0: return 'Draft'
            case 1: return 'Sent'
            case 2: return 'Viewed'
            case 3: return 'In Review'
            case 4: return 'Executed'
            case 5: return 'Cancelled'
            case 6: return 'Pending Approval'
            case 7: return 'Approved'
            case 8: return 'Declined'
            default: return 'Draft'
         }
      },

      async fetchAllProposalContracts() {
         if (!this.proposals || this.proposals.length === 0) return
         const proposalIds = this.proposals.map(p => p.id)
         const promises = proposalIds.map(id =>
            this.$axios.get(`/proposals/${id}/contracts`)
               .then(res => ({ id, contracts: res.data.vertex_contracts || [] }))
               .catch(() => ({ id, contracts: [] }))
         )
         const results = await Promise.all(promises)
         const map = {}
         results.forEach(r => { map[r.id] = r.contracts })
         this.proposalContractsMap = map
      },

      async refreshDraftPrices() {
         this.pendingAction = 'refreshDraftPrices';
         this.pendingActionData = null;
         this.confirmModalTitle = 'Refresh Draft Prices';
         this.confirmModalMessage = 'Are you sure you want to refresh prices for all draft proposals? This process will run in the background and update items to current Auth/Master rates.';
         this.showConfirmModal = true;
      },

      async onConfirmAction() {
         const action = this.pendingAction;
         const data = this.pendingActionData;
         this.pendingAction = null;
         this.pendingActionData = null;

         if (action === 'deleteProposal') {
            this.isLoading = true
            await this.$axios.delete("/proposals/" + data)
               .then((response) => {
                  if (response.data.success) {
                     this.$toast.success(response.data.message)
                     this.getProposalsList()
                     this.isLoading = false
                  } else {
                     this.$toast.error(response.data.message)
                     this.isLoading = false
                  }
               })
               .catch((error) => {
                  this.$toast.error(error.response.data.message)
                  this.isLoading = false
               })
         } else if (action === 'refreshDraftPrices') {
            this.isLoading = true;
            try {
               const response = await this.$axios.post("/proposals/draft-price-refresh");
               if (response.data.success) {
                  this.$toast.success(response.data.message || "Price refresh initiated in the background.");
               } else {
                  this.$toast.error(response.data.message || "Failed to start price refresh.");
               }
            } catch (error) {
               console.error("Price refresh error:", error);
               this.$toast.error(error.response?.data?.message || "An error occurred while starting the price refresh.");
            } finally {
               this.isLoading = false;
            }
         }
      }
   }

}

</script>

<style scoped>
/* MODIFICATION: Added new styles for nav bar */
.filterTitle {
   font-weight: 500;
   color: #435666;
}

.filterOptions {
   font-weight: 500;
   color: #00A0C1;
}

.bordered-select {
   border: 1px solid #dee2e6 !important;
   border-radius: 8px !important;
   padding-top: 0.25rem;
   padding-bottom: 0.25rem;
   padding-left: 0.75rem;
   width: auto;
}

.pagination {
   display: inline-flex;
   align-items: center;
   border: 1px solid #dee2e6;
   border-radius: 8px;
   background-color: #fff;
   overflow: hidden;
}

.pagination-label {
   font-size: 0.9rem;
   color: #6c757d;
   font-weight: 500;
   padding: 0 8px;
   white-space: nowrap;
}

.pagination-label strong {
   color: #343a40;
}

.pagination-arrow {
   background-color: transparent;
   border: none;
   cursor: pointer;
   color: #00A0C1;
   font-size: 1rem;
   padding: 8px 12px;
   transition: background-color 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
   background-color: #f1f3f5;
}

.pagination-arrow:disabled {
   color: #ced4da;
   cursor: not-allowed;
}

/* END MODIFICATION */


/* Copied and adapted styles from ProposalView.vue */
.action-button-container {
   display: flex;
   align-items: center;
   justify-content: center;
   /* Center the icons within the td */
   gap: 5px;
   /* Adjust spacing between buttons */
}

.btn-svg-icon {
   position: relative;
   width: 36px;
   height: 36px;
   border-radius: 50% !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background-color: #e6f7fa;
   color: #00A0C1;
   border: none;
   padding: 0;
   cursor: pointer;
   transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

.icon-svg {
   width: 30px;
   height: 30px;
   transition: filter 0.15s ease;
}

.icon-svg-fa {
   font-size: 16px;
   color: inherit;
   transition: color 0.15s ease;
}

.btn-svg-icon:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #fff;
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

.btn-svg-icon:active:not(:disabled) {
   transform: translateY(0);
}

.btn-svg-icon:hover:not(:disabled) .icon-svg {
   filter: brightness(0) invert(1);
}

.btn-svg-icon:hover:not(:disabled) .icon-svg-fa {
   color: #fff !important;
}

.btn-svg-icon--delete {
   background-color: #fef2f2;
   color: #ef4444;
}

.btn-svg-icon--delete:hover:not(:disabled) {
   background-color: #ef4444;
   color: #fff;
   box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
}

.btn-svg-icon:disabled {
   cursor: not-allowed;
   opacity: 0.4;
   transform: none !important;
   box-shadow: none !important;
}


/* --- Existing styles below --- */
.high {
   color: #EB1C24;
}

.low {
   color: #00A54F;
}

.medium {
   color: #F3994D;
}

.clickable-row {
   cursor: pointer;
}

td {
   padding-top: 0 !important;
   padding-bottom: 0 !important;
}

.table-select {
   padding-left: 5px;
   margin-right: 0;
   cursor: pointer;
   border: none;
   height: 45px;
   width: auto;
}

.table-select:not(.high, .low, .medium) {
   color: #435666;
}

.table-select>option {
   color: #212529;
}

.table-select:not(:hover) {
   background: none;
}

.auth-org-select {
   padding: .375rem 2.25rem .375rem 5px;
   display: block;
   width: auto;
   font-size: 1rem;
   font-weight: 400;
   color: #435666;
   border: none;
   height: 45px;
   background: none;
   min-height: unset;
   text-align: start;
   align-items: center;
}

.auth-org-select .multiselect__tags {
   width: auto;
   height: auto;
   color: #435666;
   border: none;
   padding: .375rem 0 .375rem 0;
}

.auth-org-select .multiselect__placeholder,
.multiselect--active {
   width: auto;
   margin: 0px;
   padding: .375rem 0 .375rem 0;
   color: #435666;
}

.auth-org-select .multiselect__single {
   margin: 0px;
   padding: .375rem 0 .375rem 0;
   line-height: 1;
   width: auto;
}

.auth-org-select .multiselect__input {
   /* padding: 0px; */
   line-height: 1;
   padding: .375rem 0 .375rem 5px;
   margin: 0px;
}

td {
   text-align: center;
}

th {
   text-align: center;
}

.status-text {
   font-weight: 400;
}

.text-red {
   color: #EB1C24 !important;
}

.text-green {
   color: #00A54F !important;
}

.text-orange {
   color: #F3994D !important;
}

.text-grey {
   color: #435666 !important;
}


.user-initials-icon {
   display: inline-flex;
   justify-content: center;
   align-items: center;
   width: 32px;
   height: 32px;
   border-radius: 50%;
   color: white;
   font-size: 14px;
   font-weight: bold;
   text-transform: uppercase;
   border: 1px solid rgba(255, 255, 255, 0.3);
   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
   vertical-align: middle;
}

.version-status-icon {
   display: inline-flex;
   justify-content: center;
   align-items: center;
   min-width: 45px;
   height: 24px;
   padding: 0 8px;
   border-radius: 6px;
   /* MODIFICATION: Changed from 12px to 6px */
   color: white;
   font-size: 12px;
   font-weight: bold;
   text-transform: none;
   white-space: nowrap;
   vertical-align: middle;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.activity-log-header {
   background-color: #ffffff !important;
   /* Force a white background */
   border-bottom: none;
   /* Remove the default Bootstrap border */
   padding-bottom: 0.75rem;
}

.activity-log-header .modal-title {
   font-weight: bold;
   font-size: 1.25rem;
}

.modal-divider {
   margin: 0;
   /* Remove the top/bottom margin from the <hr> element */
   color: #dee2e6;
   /* A light grey color for the line */
}

#activityLogModal .modal-body {
   height: 600px;
   /* You can change this value */
   overflow-y: auto;
   /* Ensures the scrollbar appears when needed */
}

.refresh-btn {
   height: 40px;
   border-radius: 8px;
   font-size: 14px;
   font-weight: 500;
   display: flex;
   align-items: center;
   border: 1px solid #dee2e6;
   background-color: white;
   color: #435666;
   transition: all 0.2s;
}

.refresh-btn:hover {
   background-color: #f8f9fa;
   color: #00A0C1;
   border-color: #00A0C1;
}

.refresh-btn i {
   font-size: 13px;
}

/* ===== Contract Count Badge (on action button) ===== */
.contract-count-badge {
   position: absolute;
   top: -4px;
   right: -4px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   min-width: 16px;
   height: 16px;
   padding: 0 4px;
   border-radius: 8px;
   background-color: #DC3545;
   color: white;
   font-size: 10px;
   font-weight: 700;
   line-height: 1;
}

/* ===== Contract Selection Modal (ClientProposal sample-contract style) ===== */
.contract-overlay {
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background-color: rgba(0, 0, 0, 0.7);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
}

.contract-select-modal {
   background-color: white;
   border-radius: 8px;
   width: 90%;
   max-height: 90vh;
   display: flex;
   flex-direction: column;
   box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
   max-width: 550px;
   overflow: hidden;
}

.contract-select-header {
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 1rem 1.5rem;
   border-bottom: 1px solid #e9ecef;
   flex-shrink: 0;
}

.contract-select-title {
   font-size: 1.25rem;
   font-weight: 500;
   margin: 0;
   color: #333;
}

.contract-select-close {
   background: none;
   border: none;
   font-size: 1.75rem;
   font-weight: bold;
   line-height: 1;
   color: #6c757d;
   cursor: pointer;
   opacity: 0.7;
}
.contract-select-close:hover {
   opacity: 1;
}

.contract-select-body {
   padding: 1.5rem;
   overflow-y: auto;
   flex-grow: 1;
   background-color: #fbfbfb;
}

.contract-card {
   background-color: #fff;
   border: 1px solid #dee2e6;
   border-radius: 8px;
   padding: 1rem 1.5rem;
   margin-bottom: 1rem;
   cursor: pointer;
   transition: box-shadow 0.2s ease, transform 0.2s ease;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.contract-card:last-child {
   margin-bottom: 0;
}
.contract-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
}

.contract-card-name {
   font-weight: 600;
   margin-bottom: 4px;
   color: #333;
}

.contract-card-detail {
   font-size: 0.85rem;
   color: #6c757d;
   margin-bottom: 2px;
}
.contract-card-detail:last-child {
   margin-bottom: 0;
}

.contract-no-results {
   padding: 2rem;
   text-align: center;
   color: #6c757d;
}

.btn-outline-info:focus,
.btn-outline-info:active,
.btn-outline-info.show {
   background-color: #00A0C1 !important;
   color: #ffffff !important;
   border-color: #00A0C1 !important;
}

.btn-outline-info.show .filter-text,
.btn-outline-info.show .filter-icon {
   color: #ffffff !important;
}

/* Premium Custom Checkboxes */
.custom-checkbox-input {
   position: absolute;
   opacity: 0;
   width: 0;
   height: 0;
   margin: 0;
   pointer-events: none;
}
.custom-checkbox {
   width: 20px;
   height: 20px;
   border: 2px solid #ced4da;
   border-radius: 4px;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background-color: #fff;
   transition: all 0.2s ease;
   position: relative;
}
.custom-checkbox-input:checked + .custom-checkbox {
   background-color: #00A0C1;
   border-color: #00A0C1;
}
.custom-checkbox-input:checked + .custom-checkbox::after {
   content: '';
   position: absolute;
   width: 5px;
   height: 10px;
   border: solid white;
   border-width: 0 2px 2px 0;
   transform: rotate(45deg);
   top: 2px;
   left: 6px;
}
.custom-checkbox:hover {
   border-color: #00A0C1;
}
.custom-filter-dropdown-btn {
   background-color: #ffffff !important;
   color: #495057 !important;
   border: 1px solid #dee2e6 !important;
   transition: all 0.2s ease !important;
}
.custom-filter-dropdown-btn:hover,
.custom-filter-dropdown-btn:focus,
.custom-filter-dropdown-btn:active,
.custom-filter-dropdown-btn.show {
   background-color: #f8f9fa !important;
   color: #495057 !important;
   border-color: #ced4da !important;
   box-shadow: none !important;
}
.modal-md-width {
   max-width: 900px !important;
   width: 90% !important;
   transition: max-width 0.3s ease-in-out;
}
.modal-custom-width {
   max-width: 95% !important;
   width: 95% !important;
   transition: max-width 0.3s ease-in-out;
}
.close-button-custom {
   position: absolute;
   top: 1rem;
   right: 1.5rem;
   font-size: 2rem;
   font-weight: bold;
   line-height: 1;
   color: #888;
   background: none;
   border: none;
   cursor: pointer;
   z-index: 10;
}
.close-button-custom:hover {
   color: #000;
}
.modal-title-custom {
   font-weight: bold;
   font-size: 1.25rem;
   text-align: left;
   margin-bottom: 0;
}

/* Page Header Styles */
.ms-page-header {
   margin-bottom: 24px;
   padding: 20px 24px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.ms-page-header__breadcrumb {
   font-size: 0.76rem;
   color: #557d84;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.8px;
   margin-bottom: 4px;
}

.ms-breadcrumb-sep {
   margin: 0 6px;
   color: rgba(0, 130, 152, 0.3);
}
</style>